import type {BaseOrderLine} from '../interfaces/order.line.type';
import type {ReportLine} from '../interfaces/report.line.type';
import {isUnrootedCutting} from './order.line.service';

// Capacity of poinsettia trays in a CC (Danish Cart Container).
const poinsettiaTrayCapacity = 44;

// Capacity of non-poinsettia trays in a CC (Danish Cart Container).
const nonPoinsettiaTrayCapacity = 56;

/**
 * Check if the order line is a poinsettia.
 *
 * @param orderLine The order line to check.
 */
const isGroupPoinsettia = (orderLine: BaseOrderLine): boolean => {
	return orderLine?.groupName?.toLowerCase() === 'poinsettia';
};

const isPF201 = (orderLine: BaseOrderLine): boolean => {
	return orderLine.formId === '201';
};

const isPF671 = (orderLine: BaseOrderLine): boolean => {
	return orderLine.formId === '671';
};

const isPoinsettiaPF671 = (orderLine: BaseOrderLine): boolean => {
	return isGroupPoinsettia(orderLine) && isPF671(orderLine);
};

const includeInPoinsettiaTrays = (orderLine: BaseOrderLine): boolean => {
	return isPoinsettiaPF671(orderLine) && !!orderLine.packingUnit;
};

const includeInNonPoinsettiaTrays = (orderLine: BaseOrderLine): boolean => {
	return !isPoinsettiaPF671(orderLine)
		&& !isUnrootedCutting(orderLine.formId)
		&& !isPF201(orderLine)
		&& !!orderLine.packingUnit;
};

/**
 * Check if the order lines contains poinsettias with PF671.
 *
 * @param orderLines The order lines to check.
 */
export const containsPoinsettiasWithPF671 = (orderLines: BaseOrderLine[]) => {
	if (!orderLines?.length) {
		return false;
	}

	return orderLines.some((line: BaseOrderLine) => isGroupPoinsettia(line) && isPF671(line));
};

/**
 * Calculate the amount of poinsettia trays for the given order lines.
 *
 * @param orderLines The order lines to calculate the amount of poinsettia trays for.
 */
const getAmountOfPoinsettiaTrays = (orderLines: BaseOrderLine[]): number => {
	const trays = orderLines.filter(line => includeInPoinsettiaTrays(line))
		.map(line => (line.amount / line.packingUnit))
		.reduce((sum, amount) => (sum + amount), 0);

	return Math.ceil(trays);
};

/**
 * Calculate the amount of non-poinsettia trays for the given order lines.
 *
 * @param orderLines The order lines to calculate the amount of non-poinsettia trays for.
 */
const getAmountOfNonPoinsettiaTrays = (orderLines: BaseOrderLine[]): number => {
	const trays = orderLines.filter(line => includeInNonPoinsettiaTrays(line))
		.map(line => (line.amount / line.packingUnit))
		.reduce((sum, amount) => (sum + amount), 0);

	return Math.ceil(trays);
};

/**
 * Calculate the total amount of trays for the given order lines.
 *
 * @param orderLines The order lines to calculate the total amount of trays for.
 */
export const getTotalAmountOfTrays = (orderLines: BaseOrderLine[]): number => {
	if (!orderLines?.length) {
		return 0;
	}

	const trays = orderLines.filter(line => !isUnrootedCutting(line.formId) && !isPF201(line) && line.packingUnit)
		.map(line => (line.amount / line.packingUnit))
		.reduce((sum, amount) => (sum + amount), 0);

	return Math.ceil(trays);
};

/**
 * Calculate the total amount of CC (Danish Cart Containers) for the given order lines.
 * The calculation is based on the following rules:
 * - A trolley of poinsettias can hold 44 trays.
 * - A trolley of other varieties can hold 56 trays.
 * - Any remaining space in a poinsettia trolley will be filled with non-poinsettia trays first,
 * before adding additional trolleys for non-poinsettia trays.
 *
 * @param orderLines The order lines to calculate the total amount of CC for.
 */
export const getTotalAmountOfCC = (orderLines: BaseOrderLine[]) => {
	if (!orderLines?.length) {
		return 0;
	}

	const poinsettiaTrays = getAmountOfPoinsettiaTrays(orderLines);
	let nonPoinsettiaTrays = getAmountOfNonPoinsettiaTrays(orderLines);

	const fullPoinsettiaTrolleys = Math.floor(poinsettiaTrays / poinsettiaTrayCapacity);
	const remainingPoinsettiaTrays = poinsettiaTrays % poinsettiaTrayCapacity;

	let trolleys = fullPoinsettiaTrolleys;

	if (remainingPoinsettiaTrays > 0) {
		trolleys += 1;
		const spaceLeft = poinsettiaTrayCapacity - remainingPoinsettiaTrays;
		const traysToFill = Math.min(spaceLeft, nonPoinsettiaTrays);
		nonPoinsettiaTrays -= traysToFill;
	}

	if (nonPoinsettiaTrays > 0) {
		trolleys += Math.ceil(nonPoinsettiaTrays / nonPoinsettiaTrayCapacity);
	}

	return trolleys;
};

export const getTotalAmountOfVarieties = (lines: BaseOrderLine[] | ReportLine[]): number => {
	if (!lines?.length) {
		return 0;
	}

	const allArticleIds: string[] = lines
		.filter(line => !!line.articleId)
		.map(line => line.articleId);

	return new Set(allArticleIds).size;
};
